import { DragOutlined } from '@ant-design/icons';
import { Space } from 'antd';
import clx from 'classnames';
import type React from 'react';
import styled from 'styled-components';
import {
  ContentHoverActionButtons,
  type IContentHoverActionButtonsProps,
} from './ContentHoverActionButtons.tsx';
import {
  type ContentHoverInfoAssociationType,
  ContentHoverInfoIcon,
} from './ContentHoverInfoIcon.tsx';

const ContentWrapper = styled.div`
  position: relative;
  // min-height: 44px;
  &.is-space-for-drag {
    opacity: 0;
  }
`;
const IconDragWrapper = styled.span`
  position: relative;
  font-size: 40px;
  height: 40px;
  width: 40px;
  border-radius: 50%;
  background-color: #fff;
  color: ${props => props.theme.colorPrimary};
`;

const HoverWrapper = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  overflow: hidden;
  justify-content: flex-end;
  &.is-dragging {
    justify-content: center;
  }
  align-items: center;
  padding: 2px 24px;
  margin: -8px;
  background-color: rgba(255, 255, 255, 0);
  transition: all 0.3s;
  border: 2px dashed transparent;
  border-radius: 12px;
  .hover-content,
  .hover-info {
    opacity: 0;
    transition-delay: 0.1s;
    transition: opacity 0.2s;
  }
  .hover-content {
    border-radius: 8px;
    background-color: #fff;
    box-shadow: 0px 0px 14px 10px #fff;
  }
  &:hover,
  &:focus-within,
  &.is-dragging {
    border: 2px dashed ${props => props.theme.colorPrimary};
    background-color: rgba(255, 255, 255, 0.15);
    &.is-dragging {
      background-color: rgba(255, 255, 255, 0.25);
      border-color: ${props => props.theme.colorBorder};
    }
    &.grabbable {
      cursor: grab;
      &.is-dragging {
        cursor: grabbing;
      }
    }
    .hover-content,
    .hover-info {
      opacity: 1;
    }
  }
`;

export interface ContentHoverEditButtonProps
  extends IContentHoverActionButtonsProps {
  children: React.ReactNode;
  isContainer?: boolean;
  configMode?: boolean;
  isDragging?: boolean;
  isSpaceForDrag?: boolean;
  grabbable?: boolean;
  associationType?: ContentHoverInfoAssociationType;
}

export function ContentHoverEditButton(props: ContentHoverEditButtonProps) {
  const {
    configMode,
    isDragging,
    isSpaceForDrag,
    children,
    isContainer,
    grabbable = true,
    associationType,
    extraActions,
    ...actionButtonProps
  } = props;

  return (
    <ContentWrapper className={clx({ 'is-space-for-drag': isSpaceForDrag })}>
      {isContainer && (
        <div>
          <ContentHoverActionButtons {...actionButtonProps} />
        </div>
      )}
      {(configMode ?? isDragging) && (
        <HoverWrapper
          className={clx({
            'is-dragging': isDragging,
            grabbable,
          })}
        >
          {!isDragging && (
            <ContentHoverInfoIcon
              associationType={associationType}
              className="hover-info"
            />
          )}
          <Space className="hover-content">
            {isDragging ? (
              <IconDragWrapper>
                <DragOutlined aria-hidden="true" />
              </IconDragWrapper>
            ) : (
              <ContentHoverActionButtons
                {...actionButtonProps}
                extraActions={extraActions}
              />
            )}
          </Space>
        </HoverWrapper>
      )}
      {children}
    </ContentWrapper>
  );
}
